import React from 'react';

import { Link } from 'react-router-dom';

import Box from '@liquidweb-dev/undercurrent/Box';
import LoaderCircleIcon from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import PurifyHtml from 'components/atoms/PurifyHtml';

import Button from '@liquidweb-dev/undercurrent/Button';
import ChevronLeftIcon from '@liquidweb-dev/undercurrent/icons/lucide/ChevronLeft';
import CircleXIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleX';
import TriangleAlertIcon from '@liquidweb-dev/undercurrent/icons/lucide/TriangleAlert';

import {
	CRITICAL_SEVERITY,
	ERROR_SEVERITY,
	WARNING_SEVERITY,
} from 'utility/constants/notificationConstants';

/**
 * @typedef AlertDetailsProps
 * @property {import('@liquidweb-dev/club-sauce/public/notifications/index.raw').LWApiPublicNotificationDetailsResultRawI} alert
 */

/**
 * @param {AlertDetailsProps & import('@liquidweb-dev/undercurrent/types/svgTypes').LucideSvgProps} props
 */
const StatusIcon = ({ alert, ...rest }) => {
	if ([CRITICAL_SEVERITY, ERROR_SEVERITY].includes(alert?.severity)) {
		return (
			<CircleXIcon
				sx={(theme) => ({
					color: theme.vars.palette.uc.danger.icon.main,
					flexShrink: 0,
				})}
				{...rest}
			/>
		);
	}
	if (WARNING_SEVERITY === alert?.severity) {
		return (
			<TriangleAlertIcon
				sx={(theme) => ({
					color: theme.vars.palette.uc.warning.icon.main,
					flexShrink: 0,
				})}
				{...rest}
			/>
		);
	}

	if (!alert?.enddate) {
		return (
			<LoaderCircleIcon
				sx={({ size }) => ({ height: size.xS, width: size.xS, flexShrink: 0 })}
			/>
		);
	}

	return null;
};

/**
 * @typedef AlertDetailsPropsI
 * @property {import('@liquidweb-dev/club-sauce/public/notifications/index.raw').LWApiPublicNotificationDetailsResultRawI} alert
 * @property {() => void} onActionClick
 * @property {() => void} onCloseClick
 * @property {(id: string) => void} onDismissClick
 */

/**
 * @param {AlertDetailsPropsI} props
 */
const AlertDetails = ({
	alert,
	onActionClick,
	onCloseClick,
	onDismissClick,
}) => {
	const replaceLineBreaks = (content) => content?.replace(/\n/g, '<br>');
	return (
		<>
			<Box>
				<Button
					variant="link"
					sx={{ padding: 'initial', height: 'initial' }}
					onClick={onCloseClick}
					size="small"
				>
					<ChevronLeftIcon
						sx={(theme) => ({
							color: theme.vars.palette.uc.icon.weak,
							marginRight: theme.spacing(50),
						})}
					/>
					<Typography
						variant="label-md"
						sx={(theme) => ({ color: theme.vars.palette.uc.text.weak })}
						fontWeight={500}
					>
						All alerts
					</Typography>
				</Button>
			</Box>
			<Stack flex={1} spacing={200}>
				{alert?.domain && (
					<Typography variant="body-md" fontWeight={500}>
						{alert?.domain}
					</Typography>
				)}
				{alert?.last_alert && (
					<Stack spacing={50}>
						<Typography variant="body-sm" fontWeight={500}>
							Status
						</Typography>
						<Stack direction="row" spacing={50} alignItems="center">
							<StatusIcon alert={alert} />

							<Typography component="div" variant="body-sm">
								<PurifyHtml html={replaceLineBreaks(alert?.last_alert)} />
							</Typography>
						</Stack>
					</Stack>
				)}
				{alert?.description && (
					<Stack spacing={50}>
						<Typography variant="body-sm" fontWeight={500}>
							Description
						</Typography>
						<Typography component="div" variant="body-sm">
							<PurifyHtml html={replaceLineBreaks(alert?.description)} />
						</Typography>
					</Stack>
				)}
			</Stack>
			<Stack
				sx={(theme) => ({
					position: 'sticky',
					bottom: 0,
					zIndex: 1,
					paddingTop: theme.spacing(100),
					paddingBottom: theme.spacing(200),
					backgroundColor: theme.vars.palette.uc.bg.main,
				})}
				spacing={100}
			>
				{alert?.uniq_id && (
					<Button
						component={Link}
						to={`/servers/${alert?.uniq_id}`}
						onClick={onActionClick}
					>
						Go to server
					</Button>
				)}
				<Button variant="secondary" onClick={() => onDismissClick(alert.id)}>
					Dismiss
				</Button>
			</Stack>
		</>
	);
};

export default AlertDetails;
